import config from '../internetConfig';
import utils from '../../utils';

const spellchecker = {
	/**
	 * Check a solrResponse for a spellcheck response
	 *
	 * Solr comes back with a lot of data on suggestions for the query.
	 * Here we track and return:
	 *  - suggestions: suggested search terms to try instead
	 *  - correction: a term to force instead of the one used, because it's better
	 * The logic below is to look for potential suggestions and 1 correction (if conditions met)
	 *
	 * @param {object} data         Form data
	 * @param {object} solrResponse Solr service response object
	 * @returns {object}
	 */
	check: (data, solrResponse) => {
		let results = {
			correction: null,
			suggestions: [],
			data: {},
		};

		if (!utils.isObject(solrResponse)) {
			return results;
		}

		// do not perform spellcheck when these advanced fields are used
		if (
			data.none ||
			data.all ||
			data.exact ||
			data.any ||
			data.checkedFilters?.topic.length ||
			data.checkedFilters?.audience.length
		) {
			return results;
		}

		// search spellcheck config
		let spellcheckConfig = Object.assign({}, config.spellchecker);
		let spellcheckData = solrResponse.spellcheck;

		if (!utils.isObject(spellcheckData)) {
			return solrResponse;
		}

		// Thresholds and parameters
		let mm = utils.toFloat(spellcheckConfig.spellcheckMM || 0.4);
		let wordMisspelledThreshold = utils.toNumber(spellcheckConfig.wordMisspelledThreshold || 5);
		let numFoundThreshold = utils.toNumber(spellcheckConfig.numFoundThreshold || 10);
		let strictSuggestionPercent = utils.toFloat(spellcheckConfig.strictSuggestionPercent || 0.7);
		let strictSuggestionCount = utils.toNumber(spellcheckConfig.strictSuggestionCount || 20);
		let looseSuggestionThreshold = utils.toFloat(spellcheckConfig.looseSuggestionThreshold || 0.01);
		let catalogSize = utils.toNumber(spellcheckConfig.catalogSize || 300000);

		// Tracking variables
		let collations = [];
		let suggestions = {};
		let currentNumFound = solrResponse.total; // current search response total
		let maxFound = currentNumFound;

		// Build map of suggestions for later reference.
		let origWord;
		utils.toArray(spellcheckData.suggestions || []).forEach((suggestion) => {
			if ('string' === typeof suggestion) {
				origWord = utils.cleanString(suggestion);
			}
			if (origWord && utils.isObject(suggestion)) {
				let origFreq = utils.toNumber(suggestion.origFreq);

				suggestions[origWord] = origFreq;
				let corrections = utils.toArray(suggestion.suggestion);
				corrections.forEach((correction) => {
					if (utils.isObject(correction)) {
						let correctedWord = correction.word;
						let correctedFreq = utils.toNumber(correction.freq);
						if (correctedWord && correctedFreq) {
							suggestions[correctedWord] = correctedFreq;
						}
					}
				});
			}
		});

		// Build array of collations and set maximum num found for later thresholding.
		// Removing collations that are already know to be bad.
		utils.toArray(spellcheckData.collations || []).forEach((collation) => {
			if (!utils.isObject(collation)) {
				return;
			}
			if (collation.hits > catalogSize / 2) {
				// A search that returns half the catalog in results is not really useful and is probably a stopword.
				return;
			}

			if (collation.hits > maxFound) {
				maxFound = collation.hits;
			}
			collations.push(collation);
		});

		// share metadata in response for debugging
		results.data.collations = collations;
		results.data.suggestions = suggestions;

		let maxScore = -1;

		let maxCorrectionScore = 0;
		let maxCorrectionCollation = {};

		// Score each collation and track the highest for potential correction.
		for (let index in collations) {
			let collation = collations[index];
			let suggestionTotal = utils.toNumber(collation.hits);

			let score = 0.0;
			let changedMisspelledWords = 0;
			let changedCorrectlySpelledWords = 0;

			// Calculate score based on change in total hits
			score += Math.log(suggestionTotal + 1) - Math.log(currentNumFound + 1);

			// Add scoring based on order as a proxy for edit distance (.5 for first, .25 for second, and continuing to decrease)
			score += 0.5 ** (index + 1);

			// calculate score from individual word changes
			let wordChangesArray = utils.toArray(collation.misspellingsAndCorrections);
			while (wordChangesArray.length > 1) {
				let word = wordChangesArray.shift();
				let replaceWord = wordChangesArray.shift();
				if (word && replaceWord && word in suggestions && replaceWord in suggestions) {
					if (suggestions[word] < wordMisspelledThreshold) {
						changedMisspelledWords++;
					} else {
						changedCorrectlySpelledWords++;
					}
					score += Math.log(suggestions[replaceWord] + 1) - Math.log(suggestions[word] + 1);
				}
			}
			collation.score = score;

			// update maxScore
			if (score > maxScore) {
				maxScore = score;
			}

			// Determine if it's a correction candidate and track if it's the max.
			if (
				currentNumFound < numFoundThreshold ||
				(0 < changedMisspelledWords && 0 === changedCorrectlySpelledWords)
			) {
				if (score > maxCorrectionScore) {
					maxCorrectionScore = score;
					maxCorrectionCollation = collation;
				}
			}
		}

		// Set final correction and suggestions.
		let hasCorrection = false;
		if (0 < maxCorrectionScore) {
			results.correction = utils.cleanString(maxCorrectionCollation.collationQuery);
			hasCorrection = true;
		}
		collations.sort((a, b) => {
			if (a.score === b.score) {
				return 0;
			}
			return a.score > b.score ? -1 : 1;
		});

		collations.forEach((collation) => {
			let collationQuery = utils.cleanString(collation.collationQuery);
			let inclusionThreshold = hasCorrection ? strictSuggestionPercent : looseSuggestionThreshold;

			if (!collationQuery || collationQuery === results.correction) {
				return;
			}

			if (collation.score > maxScore * inclusionThreshold) {
				results.suggestions.push(collationQuery);
			}
		});

		return results;
	},
};

export default spellchecker;
